import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import { saveAs } from "file-saver";
import MonthPickerModal from "../components/MonthPickerModal/MonthPickerModal";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import SezioneMulte from "./multe/SezioneMulte";
import "./AgenteControlloRendicontazioni.css";
import Lf23Loader from "../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Mese di default (YYYY-MM): precedente al corrente per le sezioni standard.
const ymDaData = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const mesePrecedenteYM = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return ymDaData(d);
};

const MESI_IT_FULL = [
    "Gennaio", "Febbraio", "Marzo", "Aprile",
    "Maggio", "Giugno", "Luglio", "Agosto",
    "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const periodoLabel = (ymd) => {
    if (!ymd) return "";
    const [y, m] = ymd.split("-");
    const idx = parseInt(m, 10) - 1;
    return `${MESI_IT_FULL[idx] || ""} ${y}`;
};

const LEGENDA_ORE = [
    { codice: "PT", nome: "Turno pianificato non rendicontato, con timbratura", criterio: "Esiste un turno previsto per quel giorno e almeno una timbratura, ma non sono registrate né ore lavorate né assenze giustificate.", granularita: "giornaliera" },
    { codice: "0T", nome: "Timbratura senza turno né rendicontazione", criterio: "Per quel giorno l'operatore non ha alcun turno pianificato e non ha registrato né ore lavorate né assenze giustificate, ma esiste almeno una timbratura.", granularita: "giornaliera (op-giorno)" },
    { codice: "P0", nome: "Turno pianificato non rendicontato, senza timbratura", criterio: "Esiste un turno previsto, non ci sono timbrature, non sono registrate né ore lavorate né assenze giustificate.", granularita: "giornaliera" },
    { codice: "C0", nome: "Giorno contrattuale senza rendicontazione", criterio: "Il giorno ha ore contrattuali > 0; non sono registrate né ore lavorate né assenze giustificate.", granularita: "giornaliera" },
    { codice: "C1", nome: "Eccezione weekend (modifier su C0)", criterio: "Nella coppia sabato-domenica, se un giorno ha contrattuale = 0 ed ore lavorate > 0 (le sole assenze giustificate non bastano), l'eventuale C0 sull'altro giorno della coppia viene annullata.", granularita: "giornaliera" },
    { codice: "BN", nome: "Scostamento mensile negativo", criterio: "Sul totale del mese, (ore lavorate + ore di assenza giustificata) − ore contrattuali < 0.", granularita: "mensile" },
    { codice: "R7", nome: "Riposo settimanale insufficiente", criterio: "In una finestra fissa di 14 giorni che inizia di lunedì (le finestre sono adiacenti, non sovrapposte, in sequenza continua dal primo lunedì del 2026), meno di 2 giorni di stacco. Sono giorni di stacco: (1) i giorni senza alcuna ora lavorata; (2) gli 'stacchi extra' costituiti da gap ≥ 24h tra il fine turno di un giorno e l'inizio turno del giorno successivo (entrambi lavorati). L'anomalia è attribuita al primo giorno in cui scatta la violazione.", granularita: "violazione su finestra temporale" },
    { codice: "R1", nome: "Riposo giornaliero insufficiente (< 11h)", criterio: "Tra la fine di una giornata lavorativa e l'inizio della successiva: meno di 11 ore. I turni notturni che attraversano la mezzanotte vengono uniti in un'unica giornata prima del confronto.", granularita: "violazione su finestra temporale" },
    { codice: "SG", nome: "Sforamento contrattuale giornaliero", criterio: "In un singolo giorno, (ore lavorate + ore di assenza giustificata) − ore contrattuali > 1 ora, ed entrambe le componenti sono valorizzate (ore di assenza > 0). Gli straordinari 'puri' (solo ore lavorate oltre il contratto, nessuna assenza) NON attivano l'anomalia.", granularita: "giornaliera" },
];

const LEGENDA_BUONI = [
    { codice: "A", nome: "Pasto non spettante – ore insufficienti", criterio: "L'operatore ha richiesto almeno un buono pasto in un giorno in cui ha lavorato < 7.5h (450 minuti). Diritto al buono: chi lavora almeno 7.5h (soglia inclusiva).", granularita: "giornaliera" },
    { codice: "B", nome: "Pasto non spettante – eccezione Mercatale di Ozzano", criterio: "L'operatore ha un contratto attivo nella sede 'Mercatale di Ozzano dell'Emilia' nel giorno del buono e l'ultima uscita timbrata è < 14:00. In questa sede chi termina la giornata prima delle 14:00 non ha diritto al buono, anche se lavora >= 7.5h. Esclusi sabato e domenica: nel weekend la mensa è chiusa, quindi il buono spetta comunque.", granularita: "giornaliera" },
    { codice: "C", nome: "Doppio buono pasto nello stesso giorno", criterio: "Per la stessa coppia (operatore, giorno) esistono più di un record di buono pasto. Una sola riga sintetica per giorno con il conteggio dei buoni richiesti.", granularita: "giornaliera" },
];

const LEGENDA_BUONI_NON_REND = [
    { codice: "Rendicontato", nome: "Buono pasto rendicontato su Gecos", criterio: "Nel giorno esiste almeno un buono pasto registrato su Gecos. Il conteggio riporta il numero di buoni rendicontati in quel giorno (di norma 1).", granularita: "giornaliera" },
    { codice: "Spettante", nome: "Buono pasto spettante non rendicontato", criterio: "L'operatore ha lavorato almeno 7.5h nel giorno (450 minuti) ma non ha richiesto alcun buono pasto. Per i contratti 'Mercatale di Ozzano dell'Emilia' il buono spetta solo se l'ultima uscita timbrata è >= 14:00: esclusi sia i giorni con uscita < 14:00 sia quelli senza timbratura di uscita (uscita non verificabile). È l'inverso delle regole A+B della scheda Sintesi.", granularita: "giornaliera" },
];

// La scheda Multe non ha codici-anomalia: il suo modale info mostra una guida
// descrittiva (sezioni titolo/testo) invece della tabella badge.
const GUIDA_MULTE = [
    {
        titolo: "Cos'è",
        testo: "Permette di estrarre automaticamente i dati delle multe stradali da documenti (PDF/immagini), salvarli a sistema con la relativa rateizzazione e associarli all'operatore. La scheda è divisa in due tab: Caricamento Multe e Sintesi Multe.",
    },
    {
        titolo: "Caricamento Multe",
        testo: "Si caricano uno o più file PDF/JPEG/PNG, oppure un archivio ZIP per elaborare più multe insieme. Per ogni documento vengono estratti intestatario, numero di verbale, importo totale e piano di rateizzazione. Ogni multa viene mostrata in una card con il salvataggio (verde) o l'eliminazione (rosso); 'Salva tutti' salva in blocco. La chiave è N. Verbale + Nome + Cognome: una multa già a sistema non è risalvabile (badge 'Già a sistema').",
    },
    {
        titolo: "Associazione operatore",
        testo: "L'intestatario estratto viene cercato automaticamente nell'anagrafica GECOS. Se il match non è univoco (non trovato o omonimia), sulla card compare un menu a tendina dell'anagrafica per assegnare manualmente l'operatore prima di salvare. Filiale, tipo contratto, settore e commessa derivano dal contratto attivo (o dall'ultimo) dell'operatore.",
    },
    {
        titolo: "Sintesi Multe",
        testo: "Elenca le multe salvate filtrando per mese di caricamento a sistema. I filtri (Filiale, Tipo Contratto, Settore, Commessa) sono gli stessi della Rendicontazione Ore e si applicano con 'Applica'. La tabella mostra data di caricamento, operatore e dati della multa; espandendo la riga si vede il dettaglio della rateizzazione (rata, mese, importo).",
    },
];

// Tipi colonne: type definisce la logica di sort.
//  - "string" -> localeCompare 'it-IT'
//  - "number" -> compare numerico
//  - "date"   -> compare lessicografico (assume YYYY-MM-DD)
const COLONNE_ORE_SINTESI = [
    { key: "id_operatore", label: "ID", type: "string", className: "acr-col-id" },
    { key: "nome", label: "Nome", type: "string" },
    { key: "cognome", label: "Cognome", type: "string" },
    { key: "codici_filiali", label: "Filiali", type: "string" },
    { key: "ore_lavorate_mese", label: "Ore lav.", type: "number", numeric: true, format: "ore" },
    { key: "ore_non_lavorate_mese", label: "Ore ass.", type: "number", numeric: true, format: "ore" },
    { key: "ore_contrattuali_mese", label: "Ore contr.", type: "number", numeric: true, format: "ore" },
    { key: "scostamento_mensile_ore", label: "Scost.", type: "number", numeric: true, format: "scostamento" },
    { key: "gg_PT", label: "PT", type: "number", numeric: true, badgeKey: "PT" },
    { key: "gg_0T", label: "0T", type: "number", numeric: true, badgeKey: "0T" },
    { key: "gg_P0", label: "P0", type: "number", numeric: true, badgeKey: "P0" },
    { key: "gg_C0", label: "C0", type: "number", numeric: true, badgeKey: "C0" },
    { key: "gg_C0_annullati_da_C1", label: "C0 ann. (C1)", type: "number", numeric: true, badgeKey: "C1" },
    { key: "gg_BN", label: "BN", type: "number", numeric: true, badgeKey: "BN" },
    { key: "gg_R7", label: "R7", type: "number", numeric: true, badgeKey: "R7" },
    { key: "gg_R1", label: "R1", type: "number", numeric: true, badgeKey: "R1" },
    { key: "gg_SG", label: "SG", type: "number", numeric: true, badgeKey: "SG" },
    { key: "n_contratti_nel_mese", label: "N. contr.", type: "number", numeric: true },
    { key: "lista_contratti_id", label: "Contratti ID", type: "string" },
    { key: "tipo_contratto", label: "Tipo contratto", type: "string" },
    { key: "settore", label: "Settore", type: "string" },
    { key: "commessa", label: "Commessa", type: "string" },
    { key: "ignora", label: "Ignora", type: "boolean", editable: "checkbox", notSortable: true },
    { key: "note", label: "Note", type: "string", editable: "textarea", notSortable: true },
];

const COLONNE_ORE_DETTAGLIO = [
    { key: "id_operatore", label: "ID", type: "string", className: "acr-col-id" },
    { key: "nome", label: "Nome", type: "string" },
    { key: "cognome", label: "Cognome", type: "string" },
    { key: "codice_filiale", label: "Filiale", type: "string" },
    { key: "contratto_dettaglio_id", label: "Contratto", type: "string" },
    { key: "giorno_anomalia", label: "Giorno", type: "date" },
    { key: "tipo_controllo", label: "Tipo", type: "string", isBadge: true },
    { key: "descrizione", label: "Descrizione", type: "string" },
    { key: "dettaglio", label: "Dettaglio", type: "string" },
    { key: "ignora", label: "Ignora", type: "boolean", editable: "checkbox", notSortable: true },
    { key: "note", label: "Note", type: "string", editable: "textarea", notSortable: true },
];

const COLONNE_BUONI_SINTESI = [
    { key: "id_operatore", label: "ID", type: "string", className: "acr-col-id" },
    { key: "nome", label: "Nome", type: "string" },
    { key: "cognome", label: "Cognome", type: "string" },
    { key: "n_buoni_richiesti_mese", label: "N. buoni mese", type: "number", numeric: true },
    { key: "gg_A", label: "A", type: "number", numeric: true, badgeKey: "A" },
    { key: "gg_B", label: "B", type: "number", numeric: true, badgeKey: "B" },
    { key: "gg_C", label: "C", type: "number", numeric: true, badgeKey: "C" },
    { key: "tot_infrazioni", label: "Tot. infraz.", type: "number", numeric: true },
    { key: "codici_filiali", label: "Filiali", type: "string" },
    { key: "sedi_lavoro", label: "Sedi di lavoro", type: "string" },
    { key: "tipo_contratto", label: "Tipo contratto", type: "string" },
    { key: "settore", label: "Settore", type: "string" },
    { key: "commessa", label: "Commessa", type: "string" },
    { key: "ignora", label: "Ignora", type: "boolean", editable: "checkbox", notSortable: true },
    { key: "note", label: "Note", type: "string", editable: "textarea", notSortable: true },
];

const COLONNE_BUONI_DETTAGLIO = [
    { key: "id_operatore", label: "ID", type: "string", className: "acr-col-id" },
    { key: "nome", label: "Nome", type: "string" },
    { key: "cognome", label: "Cognome", type: "string" },
    { key: "sede_lavoro", label: "Sede", type: "string" },
    { key: "giorno_anomalia", label: "Giorno", type: "date" },
    { key: "tipo_infrazione", label: "Tipo", type: "string", isBadge: true },
    { key: "descrizione", label: "Descrizione", type: "string" },
    { key: "dettaglio", label: "Dettaglio", type: "string" },
    { key: "ignora", label: "Ignora", type: "boolean", editable: "checkbox", notSortable: true },
    { key: "note", label: "Note", type: "string", editable: "textarea", notSortable: true },
];

const COLONNE_BUONI_NON_REND_SINTESI = [
    { key: "id_operatore", label: "ID", type: "string", className: "acr-col-id" },
    { key: "nome", label: "Nome", type: "string" },
    { key: "cognome", label: "Cognome", type: "string" },
    { key: "n_rendicontati", label: "Rendicontati", type: "number", numeric: true },
    { key: "gg_NR", label: "Spettanti non rend.", type: "number", numeric: true, badgeKey: "NR" },
    { key: "totale_buoni", label: "Totale buoni", type: "number", numeric: true },
    { key: "n_giorni_lavorati_mese", label: "Gg lavorati", type: "number", numeric: true },
    { key: "codici_filiali", label: "Filiali", type: "string" },
    { key: "sedi_lavoro", label: "Sedi di lavoro", type: "string" },
    { key: "tipo_contratto", label: "Tipo contratto", type: "string" },
    { key: "settore", label: "Settore", type: "string" },
    { key: "commessa", label: "Commessa", type: "string" },
    { key: "importa", label: "Gecos", editable: "importa", notSortable: true },
    { key: "ignora", label: "Ignora", type: "boolean", editable: "checkbox", notSortable: true },
    { key: "note", label: "Note", type: "string", editable: "textarea", notSortable: true },
];

const COLONNE_BUONI_NON_REND_DETTAGLIO = [
    { key: "id_operatore", label: "ID", type: "string", className: "acr-col-id" },
    { key: "nome", label: "Nome", type: "string" },
    { key: "cognome", label: "Cognome", type: "string" },
    { key: "sede_lavoro", label: "Sede", type: "string" },
    { key: "giorno_anomalia", label: "Giorno", type: "date" },
    { key: "fonte", label: "Fonte", type: "string", isBadge: true },
    { key: "n_buoni", label: "N. buoni", type: "number", numeric: true },
    { key: "ore_lavorate", label: "Ore lavorate", type: "string" },
    { key: "descrizione", label: "Descrizione", type: "string" },
    { key: "dettaglio", label: "Dettaglio", type: "string" },
    { key: "importa", label: "Gecos", editable: "importa", notSortable: true },
    { key: "ignora", label: "Ignora", type: "boolean", editable: "checkbox", notSortable: true },
    { key: "note", label: "Note", type: "string", editable: "textarea", notSortable: true },
];

const fmtOreCell = (v) => {
    if (v == null || Number.isNaN(Number(v))) return "—";
    const n = Number(v);
    if (n === Math.trunc(n)) return `${n}h`;
    return `${n.toFixed(2)}h`;
};

const fmtScostamentoCell = (v) => {
    if (v == null || Number.isNaN(Number(v))) return "—";
    const n = Number(v);
    const sign = n >= 0 ? "+" : "";
    if (n === Math.trunc(n)) return `${sign}${n}h`;
    return `${sign}${n.toFixed(2)}h`;
};

const fmtGiornoCell = (ymd) => {
    if (!ymd) return "—";
    const [y, m, d] = String(ymd).split("-");
    return `${d}/${m}/${y}`;
};

const compareValues = (a, b, type) => {
    const va = a == null ? "" : a;
    const vb = b == null ? "" : b;
    if (type === "number") {
        const na = Number(va);
        const nb = Number(vb);
        const naIs = !Number.isNaN(na);
        const nbIs = !Number.isNaN(nb);
        if (naIs && nbIs) return na - nb;
        if (naIs) return -1;
        if (nbIs) return 1;
        return 0;
    }
    // date (YYYY-MM-DD) e string: localeCompare
    return String(va).localeCompare(String(vb), "it-IT", { numeric: true });
};

// Chiave che identifica un import per-riga (deve combaciare con quella usata in
// handleImportaRiga): dettaglio = "op|giorno", sintesi = "op:<id>".
const importaRowKey = (row) =>
    row.giorno_anomalia !== undefined
        ? `${row.id_operatore}|${row.giorno_anomalia}`
        : `op:${row.id_operatore}`;

const renderCell = (col, row, onBadgeClick, onToggleIgnora, onSaveNota, onImporta, importingKey) => {
    const v = row[col.key];
    if (col.editable === "importa") {
        // Sintesi: importa tutti i giorni dell'operatore (fatto = tutti importati).
        // Dettaglio: importa il singolo giorno (fatto = riga importata).
        const isDettaglio = row.giorno_anomalia !== undefined;
        const fatto = isDettaglio ? !!row.importato : !!row.importato_tutti;
        const inCorso = importingKey != null && importingKey === importaRowKey(row);
        const altroInCorso = importingKey != null && !inCorso; // un altro import è in corso
        return (
            <button
                className={`acr-row-importa${fatto ? " fatto" : ""}`}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!fatto && !importingKey && onImporta) onImporta(row);
                }}
                disabled={fatto || !onImporta || inCorso || altroInCorso}
                title={fatto ? "Già importato su Gecos" : "Importa su Gecos"}
            >
                {inCorso ? (
                    <>
                        <span className="acr-spinner" aria-hidden="true" />
                        Importazione…
                    </>
                ) : fatto ? (
                    "Importato"
                ) : (
                    "Importa"
                )}
            </button>
        );
    }
    if (col.editable === "checkbox") {
        return (
            <label className="acr-ignora-wrapper" onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    checked={!!v}
                    onChange={() => onToggleIgnora(row)}
                />
                <span className="acr-ignora-mark" aria-hidden="true"></span>
            </label>
        );
    }
    if (col.editable === "textarea") {
        // La key deve identificare univocamente la riga: la textarea è uncontrolled
        // (defaultValue), quindi senza una key univoca il riordino "trascinerebbe" la
        // nota vecchia nella posizione. Notturne = per turno (turnoId+operatoreId);
        // dettaglio Ore/Buoni = op+giorno+tipo; sintesi = operatore.
        const noteKey = row.turnoId != null
            ? `nott-${row.operatoreId}-${row.turnoId}-${row.note || ""}-note`
            : row.giorno_anomalia
            ? `${row.id_operatore}-${row.giorno_anomalia}-${row.tipo_controllo || row.tipo_infrazione}-note`
            : `${row.id_operatore}-note`;
        return (
            <textarea
                key={noteKey}
                defaultValue={v || ""}
                onBlur={(e) => onSaveNota(row, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="acr-note-input"
                rows={2}
                placeholder="Aggiungi nota…"
            />
        );
    }
    if (col.format === "ore") return fmtOreCell(v);
    if (col.format === "scostamento") {
        const n = Number(v);
        const cls = !Number.isNaN(n) && n < 0 ? "acr-negative" : "";
        return <span className={cls}>{fmtScostamentoCell(v)}</span>;
    }
    if (col.type === "date") return fmtGiornoCell(v);
    if (col.isBadge) {
        return (
            <button className={`acr-badge acr-badge-${v}`} onClick={onBadgeClick} title="Mostra legenda">
                {v}
            </button>
        );
    }
    if (col.badgeKey && typeof v === "number" && v > 0) {
        return <span className={`acr-count acr-count-${col.badgeKey}`}>{v}</span>;
    }
    if (col.numeric && (typeof v === "number" ? v === 0 : v == null)) {
        return <span className="acr-zero">0</span>;
    }
    if (v == null || v === "") return col.numeric ? <span className="acr-zero">0</span> : "—";
    return String(v);
};

const SortIcon = ({ active, direction }) => {
    if (!active) return <span className="acr-sort-icon">⇅</span>;
    return (
        <span className="acr-sort-icon active">{direction === "asc" ? "▲" : "▼"}</span>
    );
};

const Tabella = ({
    colonne,
    righe,
    vuotoLabel,
    onBadgeClick,
    sortConfig,
    onSort,
    isFullscreen,
    onExitFullscreen,
    onToggleIgnora,
    onSaveNota,
    onImporta,
    importingKey,
    onRowClick,
}) => {
    if (!righe || righe.length === 0) {
        return <div className="acr-empty-table">{vuotoLabel}</div>;
    }
    const wrapperClass = `acr-table-wrapper${isFullscreen ? " acr-fullscreen-table" : ""}`;
    return (
        <div className={wrapperClass}>
            {isFullscreen && (
                <div className="acr-fullscreen-exit-bar">
                    <button
                        className="acr-fullscreen-btn"
                        onClick={onExitFullscreen}
                        title="Esci da tutto schermo"
                        aria-label="Esci da tutto schermo"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="4 14 10 14 10 20" />
                            <polyline points="20 10 14 10 14 4" />
                            <line x1="14" y1="10" x2="21" y2="3" />
                            <line x1="3" y1="21" x2="10" y2="14" />
                        </svg>
                    </button>
                </div>
            )}
            <div className="acr-table-scroll">
                <table className="acr-table">
                    <thead>
                        <tr>
                            {colonne.map((c) => {
                                const isActive = sortConfig.key === c.key;
                                const sortable = !c.notSortable;
                                const thClass = [
                                    sortable ? "acr-th-sortable" : "",
                                    c.numeric ? "acr-th-num" : "",
                                    c.className || "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");
                                const onClick = sortable ? () => onSort(c.key, c.type) : undefined;
                                return (
                                    <th key={c.key} className={thClass} onClick={onClick}>
                                        <span className="acr-th-content">
                                            <span>{c.label}</span>
                                            {sortable && (
                                                <SortIcon active={isActive} direction={sortConfig.direction} />
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {righe.map((row, i) => (
                            <tr
                                key={i}
                                className={onRowClick ? "acr-row-clickable" : ""}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                            >
                                {colonne.map((c) => (
                                    <td
                                        key={c.key}
                                        className={`${c.className || ""} ${c.numeric ? "acr-td-num" : ""}`}
                                    >
                                        {renderCell(c, row, onBadgeClick, onToggleIgnora, onSaveNota, onImporta, importingKey)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const Loader = ({ messaggio }) => (
    <Lf23Loader testo={messaggio || "Caricamento dati in corso..."} />
);

const ModaleLegenda = ({ aperta, voci, descrizione, titolo, onClose }) => {
    useEffect(() => {
        if (!aperta) return;
        const handleEsc = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [aperta, onClose]);

    if (!aperta) return null;
    return (
        <div className="acr-modal-overlay acr-modal-overlay-legenda" onClick={onClose}>
            <div className="acr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="acr-modal-header">
                    <div className="acr-modal-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                    </div>
                    <h3>{titolo}</h3>
                    <button className="acr-modal-close" onClick={onClose} aria-label="Chiudi">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                <div className="acr-modal-body">
                    {descrizione ? (
                        <div className="acr-guida">
                            {descrizione.map((s, i) => (
                                <div key={i} className="acr-guida-sezione">
                                    <h4>{s.titolo}</h4>
                                    <p>{s.testo}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <table className="acr-legenda-table">
                            <thead>
                                <tr>
                                    <th>Codice</th>
                                    <th>Nome</th>
                                    <th>Criterio di attivazione</th>
                                    <th>Granularità</th>
                                </tr>
                            </thead>
                            <tbody>
                                {voci.map((v) => (
                                    <tr key={v.codice}>
                                        <td>
                                            <span className={`acr-badge acr-badge-${v.codice}`}>{v.codice}</span>
                                        </td>
                                        <td>{v.nome}</td>
                                        <td>{v.criterio}</td>
                                        <td className="acr-legenda-granularita">{v.granularita}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

const ModaleDettaglioOperatore = ({
    aperto,
    operatore,
    periodoLabel: periodoTesto,
    titolo: titoloProp,
    className: classeExtra,
    colonne,
    righe,
    sortConfig,
    onSort,
    onBadgeClick,
    onToggleIgnora,
    onSaveNota,
    onImporta,
    importingKey,
    isFullscreen,
    onToggleFullscreen,
    onClose,
}) => {
    useEffect(() => {
        if (!aperto) return;
        const handleEsc = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [aperto, onClose]);

    if (!aperto || !operatore) return null;

    const modalClass = `acr-modal acr-modal-dettaglio${classeExtra ? ` ${classeExtra}` : ""}${isFullscreen ? " acr-modal-fullscreen" : ""}`;
    const titolo = titoloProp || `Dettaglio anomalie — ${operatore.nome || ""} ${operatore.cognome || ""} (ID ${operatore.id_operatore})${periodoTesto ? ` — ${periodoTesto}` : ""}`;

    return (
        <div className="acr-modal-overlay" onClick={onClose}>
            <div className={modalClass} onClick={(e) => e.stopPropagation()}>
                <div className="acr-modal-header">
                    <div className="acr-modal-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                            <polyline points="9 14 11 16 15 12" />
                        </svg>
                    </div>
                    <h3>{titolo}</h3>
                    <button
                        className="acr-fullscreen-btn"
                        onClick={onToggleFullscreen}
                        title={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}
                        aria-label={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}
                    >
                        {isFullscreen ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="4 14 10 14 10 20" />
                                <polyline points="20 10 14 10 14 4" />
                                <line x1="14" y1="10" x2="21" y2="3" />
                                <line x1="3" y1="21" x2="10" y2="14" />
                            </svg>
                        ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 3 21 3 21 9" />
                                <polyline points="9 21 3 21 3 15" />
                                <line x1="21" y1="3" x2="14" y2="10" />
                                <line x1="3" y1="21" x2="10" y2="14" />
                            </svg>
                        )}
                    </button>
                    <button className="acr-modal-close" onClick={onClose} aria-label="Chiudi">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                <div className="acr-modal-body acr-modal-body-tabella">
                    <Tabella
                        colonne={colonne}
                        righe={righe}
                        vuotoLabel="Nessuna anomalia per questo operatore con i filtri correnti."
                        onBadgeClick={onBadgeClick}
                        sortConfig={sortConfig}
                        onSort={onSort}
                        onToggleIgnora={onToggleIgnora}
                        onSaveNota={onSaveNota}
                        onImporta={onImporta}
                        importingKey={importingKey}
                    />
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// Sezione "Rendicontazione Ore Notturne" — monotab, tabella singola, no ignora.
// Vista mensile delle ore notturne (fascia 22:00-06:00) dai turni GECOS.
// Autocontenuta: stato/fetch/filtri/export propri, isolata dal resto della pagina.
// ============================================================================
const COLONNE_NOTTURNE = [
    { key: "giorno", label: "Data", type: "date" },
    { key: "operatore", label: "Operatore" },
    { key: "oraInizio", label: "Ora inizio" },
    { key: "oraFine", label: "Ora fine" },
    // Ore lavorate/notturne: display sessagesimale (h:mm); ordinamento sui minuti interi.
    { key: "oreLavorate", label: "Ore lavorate", numeric: true, sortKey: "oreLavorateMin" },
    { key: "oreNotturne", label: "Ore notturne", numeric: true, sortKey: "oreNotturneMin", className: "acr-nott-oreNotturne" },
    { key: "attivita", label: "Attività" },
    { key: "commessa", label: "Commessa" },
    { key: "settore", label: "Settore" },
    { key: "codici_filiali", label: "Filiali" },
    { key: "tipo_contratto", label: "Tipo contratto" },
    { key: "ignora", label: "Ignora", type: "boolean", editable: "checkbox", notSortable: true },
    { key: "note", label: "Note", type: "string", editable: "textarea", notSortable: true },
];

// Colonne del modale per-operatore: come sopra ma senza "Operatore" (una sola persona)
// e con etichette più corte sulle colonne strette, per evitare lo scroll orizzontale.
const LABEL_MODALE_NOTTURNE = {
    oraInizio: "Inizio",
    oraFine: "Fine",
    oreLavorate: "Ore lav.",
    oreNotturne: "Ore nott.",
    tipo_contratto: "Contratto",
};
const COLONNE_NOTTURNE_MODALE = COLONNE_NOTTURNE
    .filter((c) => c.key !== "operatore")
    .map((c) => (LABEL_MODALE_NOTTURNE[c.key] ? { ...c, label: LABEL_MODALE_NOTTURNE[c.key] } : c));

// Vista di DEFAULT: una riga per operatore col totale ore notturne (come l'export PDF).
// Ignora/Note sono per-operatore (checkbox + textarea editabili); il dettaglio per-turno
// resta nel modale.
const COLONNE_NOTTURNE_DEFAULT = [
    { key: "operatore", label: "Operatore" },
    { key: "oreNotturne", label: "Totale ore notturne", numeric: true, sortKey: "oreNotturneMin", className: "acr-nott-oreNotturne" },
    { key: "tipo_contratto", label: "Tipo contratto" },
    { key: "ignora", label: "Ignora", type: "boolean", editable: "checkbox", notSortable: true },
    { key: "note", label: "Note", editable: "textarea", notSortable: true },
];
// Chiavi ordinabili nell'aggregato (le altre sono "—" -> ripiego su operatore).
const SORT_AGG_KEYS = new Set(["operatore", "tipo_contratto", "oreNotturne"]);

// Minuti -> "H:MM" (es. 380 -> "6:20").
const minToHHMM = (min) => {
    if (min == null || Number.isNaN(Number(min))) return "—";
    const m = Math.round(Number(min));
    return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
};

// Opzioni dropdown da un campo eventualmente comma-joined ("a, b") delle righe.
const opzioniDaCampoMulti = (righe, campo) => {
    const set = new Set();
    for (const r of righe || []) {
        String(r[campo] || "")
            .split(/,\s*/)
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((v) => set.add(v));
    }
    return [...set]
        .sort((a, b) => a.localeCompare(b, "it-IT", { numeric: true }))
        .map((v) => ({ value: v, label: v }));
};

// Match OR: il valore (comma-joined) della riga intercetta almeno un selezionato.
// Stessa logica del tab Ore (matchMultiCampo).
const matchCampoMulti = (rigaValue, selezionati) => {
    if (!selezionati || selezionati.length === 0) return true;
    const sel = new Set(selezionati.map((o) => o.value));
    return String(rigaValue || "")
        .split(/,\s*/)
        .map((s) => s.trim())
        .filter(Boolean)
        .some((v) => sel.has(v));
};

const SezioneOreNotturne = () => {
    const [mese, setMese] = useState(mesePrecedenteYM);
    const [righe, setRighe] = useState(null); // null = report non ancora generato
    const [meseGenerato, setMeseGenerato] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errore, setErrore] = useState("");
    const [exporting, setExporting] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    // Filtri "bozza" (legati ai dropdown)
    const [operatoriSel, setOperatoriSel] = useState([]);
    const [commesseSel, setCommesseSel] = useState([]);
    const [filialiSel, setFilialiSel] = useState([]);
    const [tipiContrattoSel, setTipiContrattoSel] = useState([]);
    const [settoriSel, setSettoriSel] = useState([]);
    const [ignoratiSel, setIgnoratiSel] = useState("tutti"); // "tutti" | "no" | "si"
    // Filtri applicati alla tabella (commit su "Applica")
    const [filtriAppl, setFiltriAppl] = useState({
        operatori: [], commesse: [], filiali: [], tipiContratto: [], settori: [], ignorati: "tutti",
    });
    const [sortConfig, setSortConfig] = useState({ key: "giorno", dir: "asc" });
    // Modale per-operatore (group-by): null = chiuso, altrimenti { operatoreId, operatore }.
    const [operatoreModale, setOperatoreModale] = useState(null);
    const [modaleFullscreen, setModaleFullscreen] = useState(false);
    const [tabellaFullscreen, setTabellaFullscreen] = useState(false);

    // AbortController della richiesta in corso: si annulla quando ne parte una nuova o
    // quando si lascia la pagina (unmount), cosi' il pull su Gecos non resta orfano.
    const abortRef = useRef(null);

    const handleApplica = async () => {
        if (!mese || loading) return;
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setErrore("");
        try {
            const { data } = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/ore-notturne`,
                { mese },
                { signal: controller.signal }
            );
            setRighe(Array.isArray(data) ? data : []);
            setMeseGenerato(mese);
            setFiltriAppl({
                operatori: operatoriSel,
                commesse: commesseSel,
                filiali: filialiSel,
                tipiContratto: tipiContrattoSel,
                settori: settoriSel,
                ignorati: ignoratiSel,
            });
            setSortConfig({ key: "giorno", dir: "asc" });
            setOperatoreModale(null); // chiudi un eventuale modale su dati vecchi
        } catch (err) {
            if (axios.isCancel(err) || err.code === "ERR_CANCELED") return; // richiesta annullata: non e' un errore
            setErrore(err.response?.data?.error || err.message || "Errore durante la generazione");
            setRighe(null);
        } finally {
            if (abortRef.current === controller) setLoading(false);
        }
    };

    // Nessun auto-caricamento all'atterraggio: il report parte solo su "Applica" (evita
    // generazioni pesanti non richieste). All'unmount annulla l'eventuale richiesta in volo.
    useEffect(() => {
        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, []);

    // ESC esce dal fullscreen tabella (ma non se c'è un modale aperto: quello gestisce l'ESC per sé).
    useEffect(() => {
        if (!tabellaFullscreen) return;
        const onEsc = (e) => {
            if (e.key === "Escape" && !operatoreModale) setTabellaFullscreen(false);
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [tabellaFullscreen, operatoreModale]);

    const opzioniOperatore = useMemo(() => {
        if (!righe) return [];
        const set = new Set(righe.map((r) => r.operatore).filter(Boolean));
        return [...set].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
    }, [righe]);

    const opzioniCommessa = useMemo(() => {
        if (!righe) return [];
        const set = new Set(righe.map((r) => r.commessa).filter(Boolean));
        return [...set].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
    }, [righe]);

    // Filtri allineati al tab Ore (stessa logica matchCampoMulti / stessi nomi campo).
    const opzioniFiliale = useMemo(() => opzioniDaCampoMulti(righe, "codici_filiali"), [righe]);
    const opzioniTipoContratto = useMemo(() => opzioniDaCampoMulti(righe, "tipo_contratto"), [righe]);
    const opzioniSettore = useMemo(() => opzioniDaCampoMulti(righe, "settore"), [righe]);

    const filtered = useMemo(() => {
        if (!righe) return [];
        const { operatori, commesse, filiali, tipiContratto, settori } = filtriAppl;
        const opSet = new Set(operatori.map((o) => o.value));
        const coSet = new Set(commesse.map((o) => o.value));
        // NB: il filtro "Ignorati" NON si applica qui: agisce sul flag PER-OPERATORE
        // dell'aggregato. Qui restano tutti i turni (il modale li mostra tutti).
        return righe.filter((r) => {
            if (opSet.size && !opSet.has(r.operatore)) return false;
            if (coSet.size && !coSet.has(r.commessa)) return false;
            if (!matchCampoMulti(r.codici_filiali, filiali)) return false;
            if (!matchCampoMulti(r.tipo_contratto, tipiContratto)) return false;
            if (!matchCampoMulti(r.settore, settori)) return false;
            return true;
        });
    }, [righe, filtriAppl]);

    const sorted = useMemo(() => {
        const { key, dir } = sortConfig;
        const col = COLONNE_NOTTURNE.find((c) => c.key === key);
        const field = col?.sortKey || key;
        const mul = dir === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const va = a[field], vb = b[field];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (col?.numeric) return (va - vb) * mul;
            return String(va).localeCompare(String(vb)) * mul;
        });
    }, [filtered, sortConfig]);

    // Vista di default: una riga per operatore. Il totale ESCLUDE i turni ignorati
    // per-turno (come il PDF); ignora/note sono il flag PER-OPERATORE. Il dettaglio
    // per-turno (coi suoi ignora/note) è nel modale.
    const aggregato = useMemo(() => {
        const byOp = new Map();
        for (const r of filtered) {
            if (r.operatoreId == null) continue;
            const id = String(r.operatoreId);
            let e = byOp.get(id);
            if (!e) {
                e = {
                    operatoreId: r.operatoreId,
                    operatore: r.operatore,
                    tipi: new Set(),
                    oreNotturneMin: 0,
                    ignora: !!r.ignora_operatore,
                    note: r.note_operatore || "",
                };
                byOp.set(id, e);
            }
            if (!r.ignora) e.oreNotturneMin += r.oreNotturneMin || 0; // turni ignorati fuori dal totale
            String(r.tipo_contratto || "")
                .split(/,\s*/)
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((t) => e.tipi.add(t));
        }
        return [...byOp.values()].map((e) => ({
            operatoreId: e.operatoreId,
            id_operatore: e.operatoreId, // per la key della textarea (renderCell)
            operatore: e.operatore,
            tipo_contratto: [...e.tipi].sort((a, b) => a.localeCompare(b)).join(", "),
            ignora: e.ignora,
            note: e.note,
            oreNotturneMin: e.oreNotturneMin,
            oreNotturne: minToHHMM(e.oreNotturneMin),
        }));
    }, [filtered]);

    // Filtro "Ignorati" (Tutti/No/Sì) applicato al flag PER-OPERATORE (come Buoni Pasto).
    const aggregatoVisible = useMemo(
        () => applicaFiltroIgnorati(aggregato, filtriAppl.ignorati || "tutti"),
        [aggregato, filtriAppl]
    );

    const aggregatoSorted = useMemo(() => {
        const key = SORT_AGG_KEYS.has(sortConfig.key) ? sortConfig.key : "operatore";
        const dir = SORT_AGG_KEYS.has(sortConfig.key) ? sortConfig.dir : "asc";
        const col = COLONNE_NOTTURNE_DEFAULT.find((c) => c.key === key);
        const field = col?.sortKey || key;
        const mul = dir === "asc" ? 1 : -1;
        return [...aggregatoVisible].sort((a, b) => {
            const va = a[field], vb = b[field];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (col?.numeric) return (va - vb) * mul;
            return String(va).localeCompare(String(vb)) * mul;
        });
    }, [aggregatoVisible, sortConfig]);

    // Totale complessivo = somma dei totali degli operatori visibili.
    const totaleNotturneMin = useMemo(
        () => aggregatoVisible.reduce((acc, r) => acc + (r.oreNotturneMin || 0), 0),
        [aggregatoVisible]
    );

    // Righe del modale = i turni dell'operatore selezionato, nell'ordine corrente.
    // È solo una vista filtrata di `sorted`, quindi ignora/note editati qui si
    // riflettono anche nella tabella sotto (stesso array in `righe`).
    const righeModale = useMemo(() => {
        if (!operatoreModale) return [];
        return sorted.filter((r) => String(r.operatoreId) === String(operatoreModale.operatoreId));
    }, [sorted, operatoreModale]);

    const onSort = (key) =>
        setSortConfig((p) => (p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

    const handleExport = async () => {
        if (exporting || !meseGenerato || sorted.length === 0) return;
        setExporting(true);
        try {
            const visIds = new Set(aggregatoSorted.map((r) => String(r.operatoreId)));
            const rowsExport = sorted.filter((r) => visIds.has(String(r.operatoreId)));
            const res = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/ore-notturne/export`,
                { mese: meseGenerato, rows: rowsExport },
                { responseType: "blob" }
            );
            saveAs(res.data, `rendicontazione_ore_notturne_${meseGenerato}.xlsx`);
        } catch (err) {
            setErrore("Errore nell'esportazione Excel.");
        } finally {
            setExporting(false);
        }
    };

    // PDF: group-by per operatore + somma ore notturne, senza ignorati. A differenza
    // dell'Excel, IGNORA i filtri: manda `righe` (tutto il mese generato), non `sorted`.
    // L'aggregazione e l'esclusione degli ignorati le fa il backend.
    const handleExportPdf = async () => {
        if (exportingPdf || !meseGenerato || !righe || righe.length === 0) return;
        setExportingPdf(true);
        try {
            const res = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/ore-notturne/export-pdf`,
                { mese: meseGenerato, rows: righe },
                { responseType: "blob" }
            );
            saveAs(res.data, `rendicontazione_ore_notturne_${meseGenerato}.pdf`);
        } catch (err) {
            setErrore("Errore nell'esportazione PDF.");
        } finally {
            setExportingPdf(false);
        }
    };

    // --- Ignora/Note per turno (chiave operatoreId+turnoId, come nel backend) ---
    // Aggiorna la riga in `righe` (la modifica si riflette anche nel modale, che è
    // solo una vista filtrata dello stesso array) e persiste in background.
    const patchRiga = (operatoreId, turnoId, patch) => {
        setRighe((prev) =>
            (prev || []).map((r) =>
                String(r.operatoreId) === String(operatoreId) && String(r.turnoId) === String(turnoId)
                    ? { ...r, ...patch }
                    : r
            )
        );
    };

    const postIgnora = async (row, patch) => {
        try {
            await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/ore-notturne/ignora`,
                {
                    mese: meseGenerato,
                    id_operatore: row.operatoreId,
                    turno_id: row.turnoId,
                    ignora: patch.ignora !== undefined ? patch.ignora : !!row.ignora,
                    note: patch.note !== undefined ? patch.note : row.note || "",
                }
            );
        } catch (err) {
            setErrore(err.response?.data?.error || err.message || "Errore salvataggio");
        }
    };

    const handleToggleIgnora = (row) => {
        if (row.operatoreId == null || row.turnoId == null) return; // turno senza operatore: non persistibile
        const nuovo = !row.ignora;
        patchRiga(row.operatoreId, row.turnoId, { ignora: nuovo });
        postIgnora(row, { ignora: nuovo });
    };

    const handleSaveNota = (row, valore) => {
        if (row.operatoreId == null || row.turnoId == null) return;
        if ((row.note || "") === valore) return;
        patchRiga(row.operatoreId, row.turnoId, { note: valore });
        postIgnora(row, { note: valore });
    };

    // --- Ignora/Note PER-OPERATORE (vista aggregata) ---
    // Aggiorna i campi *_operatore su TUTTI i turni dell'operatore in `righe`
    // (l'aggregato li rilegge) e persiste in background sull'endpoint per-operatore.
    const patchOperatore = (operatoreId, patch) => {
        setRighe((prev) =>
            (prev || []).map((r) =>
                String(r.operatoreId) === String(operatoreId) ? { ...r, ...patch } : r
            )
        );
    };

    const postIgnoraOperatore = async (operatoreId, ignora, note) => {
        try {
            await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/ore-notturne/ignora-operatore`,
                { mese: meseGenerato, id_operatore: operatoreId, ignora, note }
            );
        } catch (err) {
            setErrore(err.response?.data?.error || err.message || "Errore salvataggio");
        }
    };

    const handleToggleIgnoraOp = (row) => {
        if (row.operatoreId == null) return;
        const nuovo = !row.ignora;
        patchOperatore(row.operatoreId, { ignora_operatore: nuovo });
        postIgnoraOperatore(row.operatoreId, nuovo, row.note || "");
    };

    const handleSaveNotaOp = (row, valore) => {
        if (row.operatoreId == null) return;
        if ((row.note || "") === valore) return;
        patchOperatore(row.operatoreId, { note_operatore: valore });
        postIgnoraOperatore(row.operatoreId, !!row.ignora, valore);
    };

    return (
        <>
            <div className="acr-panel">
                <div className="acr-filters-grid">
                    <div className="acr-filter-item">
                        <label>Periodo</label>
                        <MonthPickerModal
                            value={mese}
                            onChange={(e) => setMese(e.target.value)}
                            label="Seleziona mese"
                            min="2026-01"
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Operatore</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniOperatore}
                            value={operatoriSel}
                            onChange={(vals) => setOperatoriSel(vals || [])}
                            placeholder={righe ? "Tutti" : "Genera report…"}
                            noOptionsMessage={() => "Nessun operatore"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Filiale</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniFiliale}
                            value={filialiSel}
                            onChange={(vals) => setFilialiSel(vals || [])}
                            placeholder={righe ? "Tutte" : "Genera report…"}
                            noOptionsMessage={() => "Nessuna filiale"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Tipo Contratto</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniTipoContratto}
                            value={tipiContrattoSel}
                            onChange={(vals) => setTipiContrattoSel(vals || [])}
                            placeholder={righe ? "Tutti" : "Genera report…"}
                            noOptionsMessage={() => "Nessun tipo"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Settore</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniSettore}
                            value={settoriSel}
                            onChange={(vals) => setSettoriSel(vals || [])}
                            placeholder={righe ? "Tutti" : "Genera report…"}
                            noOptionsMessage={() => "Nessun settore"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Commessa</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniCommessa}
                            value={commesseSel}
                            onChange={(vals) => setCommesseSel(vals || [])}
                            placeholder={righe ? "Tutte" : "Genera report…"}
                            noOptionsMessage={() => "Nessuna commessa"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Ignorati</label>
                        <Select
                            isDisabled={!righe}
                            options={OPZIONI_IGNORATI}
                            value={
                                OPZIONI_IGNORATI.find((o) => o.value === ignoratiSel) ||
                                OPZIONI_IGNORATI[0]
                            }
                            onChange={(opt) => setIgnoratiSel(opt ? opt.value : "tutti")}
                            isSearchable={false}
                            classNamePrefix="acr-select"
                        />
                    </div>
                    <div className="acr-filter-actions">
                        <button className="acr-btn-primary" onClick={handleApplica} disabled={!mese || loading}>
                            {loading ? "Caricamento…" : "Applica"}
                        </button>
                    </div>
                </div>
            </div>

            {errore && (
                <div className="acr-errore">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    {errore}
                </div>
            )}

            {loading && <Loader messaggio={`Analisi ore notturne per ${periodoLabel(mese)}…`} />}

            {!loading && !righe && !errore && (
                <div className="acr-empty-state">
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                        <polyline points="9 14 11 16 15 12" />
                    </svg>
                    <p>Seleziona il periodo e premi <strong>Applica</strong> per aggiornare l'analisi.</p>
                </div>
            )}

            {!loading && righe && (
                <>
                    <div className="acr-info-periodo">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        Report del periodo <strong>{periodoLabel(meseGenerato)}</strong>
                        {" — "}
                        {aggregatoSorted.length} operatori · {minToHHMM(totaleNotturneMin)} ore notturne
                    </div>

                    <div className="acr-toolbar-sintesi">
                        <div className="acr-toolbar-title">
                            {`Operatori (${aggregatoSorted.length})`}
                        </div>
                        <div className="acr-actions-right">
                            <button
                                className="acr-export-btn acr-export-btn-pdf"
                                onClick={handleExportPdf}
                                disabled={exportingPdf || righe.length === 0}
                                title="Esporta in PDF il riepilogo per operatore di tutto il mese (esclusi gli ignorati; ignora i filtri)"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="9" y1="13" x2="15" y2="13" />
                                    <line x1="9" y1="17" x2="15" y2="17" />
                                </svg>
                                {exportingPdf ? "Esportazione…" : "Esporta PDF"}
                            </button>
                            <button
                                className="acr-export-btn"
                                onClick={handleExport}
                                disabled={exporting || sorted.length === 0}
                                title="Esporta i dati in Excel"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                {exporting ? "Esportazione…" : "Esporta Excel"}
                            </button>
                            <button
                                className="acr-fullscreen-btn"
                                onClick={() => setTabellaFullscreen(true)}
                                title="Tutto schermo"
                                aria-label="Tutto schermo"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 3 21 3 21 9" />
                                    <polyline points="9 21 3 21 3 15" />
                                    <line x1="21" y1="3" x2="14" y2="10" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <RotateDevice active={tabellaFullscreen} />
                    <Tabella
                        colonne={COLONNE_NOTTURNE_DEFAULT}
                        righe={aggregatoSorted}
                        vuotoLabel="Nessun operatore con ore notturne in questo periodo."
                        sortConfig={{ key: sortConfig.key, direction: sortConfig.dir }}
                        onSort={onSort}
                        isFullscreen={tabellaFullscreen}
                        onExitFullscreen={() => setTabellaFullscreen(false)}
                        onToggleIgnora={handleToggleIgnoraOp}
                        onSaveNota={handleSaveNotaOp}
                        onRowClick={(row) =>
                            row.operatoreId != null &&
                            setOperatoreModale({ operatoreId: row.operatoreId, operatore: row.operatore })
                        }
                    />
                </>
            )}

            <ModaleDettaglioOperatore
                aperto={!!operatoreModale}
                operatore={
                    operatoreModale
                        ? { id_operatore: operatoreModale.operatoreId, nome: operatoreModale.operatore, cognome: "" }
                        : null
                }
                titolo={
                    operatoreModale
                        ? `Turni notturni — ${operatoreModale.operatore || "—"} (ID ${operatoreModale.operatoreId}) — ${periodoLabel(meseGenerato)}`
                        : ""
                }
                className="acr-modal-notturne"
                colonne={COLONNE_NOTTURNE_MODALE}
                righe={righeModale}
                sortConfig={{ key: sortConfig.key, direction: sortConfig.dir }}
                onSort={onSort}
                onToggleIgnora={handleToggleIgnora}
                onSaveNota={handleSaveNota}
                isFullscreen={modaleFullscreen}
                onToggleFullscreen={() => setModaleFullscreen((v) => !v)}
                onClose={() => {
                    setOperatoreModale(null);
                    setModaleFullscreen(false);
                }}
            />
        </>
    );
};

// Filtro "Ignorati": Tutti (default) | No (solo non ignorati) | Sì (solo ignorati)
const OPZIONI_IGNORATI = [
    { value: "tutti", label: "Tutti" },
    { value: "no", label: "No" },
    { value: "si", label: "Sì" },
];

const applicaFiltroIgnorati = (rows, modo) => {
    if (modo === "si") return rows.filter((r) => r.ignora);
    if (modo === "no") return rows.filter((r) => !r.ignora);
    return rows; // "tutti"
};

const AgenteControlloRendicontazioni = () => {
    const [sezione, setSezione] = useState("ore");
    const [periodoOre, setPeriodoOre] = useState(mesePrecedenteYM);
    const [periodoBuoni, setPeriodoBuoni] = useState(mesePrecedenteYM);
    const [loading, setLoading] = useState({ ore: false, buoniPasto: false, buoniNonRend: false });
    const [errore, setErrore] = useState({ ore: "", buoniPasto: "", buoniNonRend: "" });
    const [dati, setDati] = useState({ ore: null, buoniPasto: null, buoniNonRend: null });
    const [periodoGenerato, setPeriodoGenerato] = useState({ ore: null, buoniPasto: null, buoniNonRend: null });
    const [legendaAperta, setLegendaAperta] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    // Filtri dropdown per-sezione (array di opzioni { value, label } selezionate).
    // "ignorati" è un filtro a valore singolo: "tutti" | "no" | "si".
    const [filtri, setFiltri] = useState({
        ore: { filiale: [], tipoContratto: [], settore: [], commessa: [], ignorati: "tutti" },
        buoniPasto: { filiale: [], tipoContratto: [], sedeLavoro: [], settore: [], commessa: [], ignorati: "tutti" },
        buoniNonRend: { filiale: [], tipoContratto: [], sedeLavoro: [], settore: [], commessa: [], ignorati: "tutti" },
    });
    // Filtri effettivamente applicati alla tabella. I dropdown sopra sono solo
    // "bozza": vengono applicati (copiati qui) solo al click di "Applica".
    const [filtriApplicati, setFiltriApplicati] = useState({
        ore: { filiale: [], tipoContratto: [], settore: [], commessa: [], ignorati: "tutti" },
        buoniPasto: { filiale: [], tipoContratto: [], sedeLavoro: [], settore: [], commessa: [], ignorati: "tutti" },
        buoniNonRend: { filiale: [], tipoContratto: [], sedeLavoro: [], settore: [], commessa: [], ignorati: "tutti" },
    });
    // Ricerca testuale live sulla tabella Sintesi (nome, cognome, id operatore).
    // A differenza dei dropdown NON passa da "Applica": filtra all'istante mentre digiti.
    // Per-sezione, così ogni scheda ricorda il proprio testo di ricerca.
    const [ricercaOperatore, setRicercaOperatore] = useState({ ore: "", buoniPasto: "", buoniNonRend: "" });
    // Modale Dettaglio per-operatore: null = chiuso, altrimenti { id_operatore, nome, cognome }
    const [operatoreModale, setOperatoreModale] = useState(null);
    const [modaleFullscreen, setModaleFullscreen] = useState(false);
    // Import buoni su Gecos (solo sezione buoniNonRend): stato in corso + messaggio esito.
    const [importingGecos, setImportingGecos] = useState(false);
    // Quale import è in corso: "__bulk__" oppure la chiave di riga (vedi importaRowKey).
    // Serve a mostrare lo spinner sulla riga/bottone giusto e bloccare gli altri.
    const [importingKey, setImportingKey] = useState(null);
    const [importMsg, setImportMsg] = useState(null); // { ok: bool, testo: string }
    // Conferma prima di scrivere su Gecos: null = chiuso, altrimenti { records, key, messaggio }.
    const [confirmImport, setConfirmImport] = useState(null);

    // AbortController PER-SEZIONE della generazione in corso: annullata quando ne riparte
    // una per la STESSA sezione o quando si lascia la pagina (unmount), cosi' il pull su Gecos
    // non resta orfano. Per-sezione (non condiviso) cosi' generare un'altra scheda non annulla
    // ne' lascia appeso il loading di quella che stava gia' caricando.
    const controllersRef = useRef({}); // sezione -> AbortController
    useEffect(() => {
        const controllers = controllersRef.current;
        return () => {
            Object.values(controllers).forEach((c) => c && c.abort());
        };
    }, []);

    useEffect(() => {
        if (!isFullscreen) return;
        const handleEsc = (e) => {
            // Quando il modale Dettaglio e' aperto, l'ESC viene gestito dal modale.
            if (e.key === "Escape" && !operatoreModale) setIsFullscreen(false);
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [isFullscreen, operatoreModale]);

    // sortConfig per sezione+sub-tab (6 chiavi)
    const [sortConfig, setSortConfig] = useState({
        ore_sintesi: { key: null, direction: null, type: null },
        ore_dettaglio: { key: null, direction: null, type: null },
        buoniPasto_sintesi: { key: null, direction: null, type: null },
        buoniPasto_dettaglio: { key: null, direction: null, type: null },
        buoniNonRend_sintesi: { key: null, direction: null, type: null },
        buoniNonRend_dettaglio: { key: null, direction: null, type: null },
    });

    // buoniNonRend condivide il mese con la scheda Buoni Pasto (stessa periodoBuoni).
    const periodo = sezione === "ore" ? periodoOre : periodoBuoni;
    const setPeriodo = sezione === "ore" ? setPeriodoOre : setPeriodoBuoni;

    const ENDPOINT_SEZIONE = {
        ore: "/agente-controllo-rendicontazioni/ore",
        buoniPasto: "/agente-controllo-rendicontazioni/buoni-pasto",
        buoniNonRend: "/agente-controllo-rendicontazioni/buoni-non-rend",
    };

    // "Applica": (ri)carica il mese selezionato per la sezione e applica i filtri
    // bozza correnti. Usata anche dall'auto-caricamento all'atterraggio (con
    // filtri vuoti). Accetta una sezione/periodo espliciti per l'auto-load.
    const handleApplica = async (sez = sezione, mese = periodo) => {
        if (!mese) return;
        const endpoint = ENDPOINT_SEZIONE[sez];
        if (!endpoint) return;

        if (controllersRef.current[sez]) controllersRef.current[sez].abort();
        const controller = new AbortController();
        controllersRef.current[sez] = controller;
        setLoading((p) => ({ ...p, [sez]: true }));
        setErrore((p) => ({ ...p, [sez]: "" }));
        try {
            const { data } = await axios.post(`${API_URL}${endpoint}`, { mese }, { signal: controller.signal });
            setDati((p) => ({ ...p, [sez]: data }));
            setPeriodoGenerato((p) => ({ ...p, [sez]: mese }));
            // applico i filtri bozza correnti alla tabella
            setFiltriApplicati((p) => ({ ...p, [sez]: filtri[sez] }));
            // chiudo eventuale modale dettaglio aperto su dati vecchi
            setOperatoreModale(null);
            setModaleFullscreen(false);
            // reset sort per la sezione corrente
            const k1 = `${sez}_sintesi`;
            const k2 = `${sez}_dettaglio`;
            setSortConfig((p) => ({
                ...p,
                [k1]: { key: null, direction: null, type: null },
                [k2]: { key: null, direction: null, type: null },
            }));
        } catch (err) {
            if (axios.isCancel(err) || err.code === "ERR_CANCELED") return; // richiesta annullata: non e' un errore
            const msg = err.response?.data?.error || err.message || "Errore durante la generazione";
            setErrore((p) => ({ ...p, [sez]: msg }));
        } finally {
            if (controllersRef.current[sez] === controller) setLoading((p) => ({ ...p, [sez]: false }));
        }
    };

    // Marca come importati i dettagli scritti e ricalcola i flag di Sintesi
    // (gg_importati / importato_tutti) senza ricaricare (update ottimistico).
    const applicaImportato = (writtenKeys) => {
        setDati((prev) => {
            const corr = prev.buoniNonRend;
            if (!corr) return prev;
            const dett = (corr.dettaglio || []).map((r) =>
                writtenKeys.has(`${r.id_operatore}|${r.giorno_anomalia}`) ? { ...r, importato: true } : r
            );
            const perOp = new Map();
            for (const r of dett) {
                const e = perOp.get(String(r.id_operatore)) || { tot: 0, imp: 0 };
                e.tot += 1;
                if (r.importato) e.imp += 1;
                perOp.set(String(r.id_operatore), e);
            }
            const sint = (corr.sintesi || []).map((row) => {
                const e = perOp.get(String(row.id_operatore)) || { tot: 0, imp: 0 };
                return { ...row, gg_importati: e.imp, importato_tutti: e.tot > 0 && e.imp === e.tot };
            });
            return { ...prev, buoniNonRend: { ...corr, dettaglio: dett, sintesi: sint } };
        });
    };

    // Scrive su Gecos i buoni per i `records` ({ id_operatore, giorno }) passati.
    // `key` identifica l'azione in corso ("__bulk__" o chiave riga) per lo spinner.
    const handleImportaGecos = async (records, key = "__bulk__") => {
        if (importingGecos || !records || records.length === 0 || !periodoGeneratoCorrente) return;
        setImportingGecos(true);
        setImportingKey(key);
        setImportMsg(null);
        try {
            const { data } = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/buoni-non-rend/importa-gecos`,
                { mese: periodoGeneratoCorrente, records }
            );
            const scritti = data.scritti || [];
            const saltati = data.saltati || [];
            const falliti = data.falliti || [];
            applicaImportato(new Set(scritti.map((s) => `${s.id_operatore}|${s.giorno}`)));
            const parti = [`${scritti.length} scritti`];
            if (saltati.length) parti.push(`${saltati.length} già presenti`);
            if (falliti.length) parti.push(`${falliti.length} falliti`);
            setImportMsg({ ok: falliti.length === 0, testo: `Import Gecos: ${parti.join(", ")}.` });
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Errore durante l'import su Gecos";
            setImportMsg({ ok: false, testo: msg });
        } finally {
            setImportingGecos(false);
            setImportingKey(null);
        }
    };

    // Record da importare per l'operatore (tutti i suoi giorni NR non ignorati e non già importati).
    const recordsOperatore = (idOperatore) =>
        (datiCorrenti?.dettaglio || [])
            .filter((r) => String(r.id_operatore) === String(idOperatore) && !r.ignora && !r.importato)
            .map((r) => ({ id_operatore: r.id_operatore, giorno: r.giorno_anomalia }));

    // Apre il modale di conferma prima di scrivere su Gecos (azione su sistema esterno,
    // non reversibile da qui). L'import vero e proprio parte solo al click su "Conferma".
    const apriConfermaImport = (records, key, messaggio) => {
        if (!records || records.length === 0) return;
        setConfirmImport({ records, key, messaggio });
    };

    const confermaImportGecos = () => {
        if (!confirmImport) return;
        const { records, key } = confirmImport;
        setConfirmImport(null);
        handleImportaGecos(records, key);
    };

    // Bottone "Importa" per-riga: Sintesi -> tutti i giorni dell'operatore;
    // Dettaglio (riga con giorno_anomalia) -> singolo buono.
    const handleImportaRiga = (row) => {
        const nomeCompleto = `${row.nome || ""} ${row.cognome || ""}`.trim();
        if (row.giorno_anomalia !== undefined) {
            if (row.importato) return;
            apriConfermaImport(
                [{ id_operatore: row.id_operatore, giorno: row.giorno_anomalia }],
                `${row.id_operatore}|${row.giorno_anomalia}`,
                `Stai per scrivere su Gecos il buono pasto di ${nomeCompleto} del ${fmtGiornoCell(row.giorno_anomalia)}. Continuare?`
            );
        } else {
            const records = recordsOperatore(row.id_operatore);
            apriConfermaImport(
                records,
                `op:${row.id_operatore}`,
                `Stai per scrivere su Gecos ${records.length} buon${records.length === 1 ? "o" : "i"} pasto per ${nomeCompleto}. Continuare?`
            );
        }
    };

    // Nessun auto-caricamento all'atterraggio ne' al cambio sezione: il report parte solo su
    // "Applica". Evita generazioni pesanti (~400 pagine Gecos) non richieste che, in concorrenza,
    // saturavano il rate-limit del WAF. L'utente vede l'empty-state "premi Applica".

    const datiCorrenti = dati[sezione];
    const loadingCorrente = loading[sezione];
    const erroreCorrente = errore[sezione];
    const periodoGeneratoCorrente = periodoGenerato[sezione];

    // Sub-tab attivo: derivato dallo stato del modale (aperto -> "dettaglio").
    const subTabAttivo = operatoreModale ? "dettaglio" : "sintesi";
    const sortKeySintesi = `${sezione}_sintesi`;
    const sortKeyDettaglio = `${sezione}_dettaglio`;
    const sortKey = `${sezione}_${subTabAttivo}`;
    const sortCorrente = sortConfig[sortKey];
    const sortSintesi = sortConfig[sortKeySintesi];
    const sortDettaglio = sortConfig[sortKeyDettaglio];

    const handleSort = (key, type) => {
        setSortConfig((prev) => {
            const cur = prev[sortKey];
            let next;
            if (cur.key !== key) {
                next = { key, direction: "asc", type };
            } else if (cur.direction === "asc") {
                next = { key, direction: "desc", type };
            } else {
                next = { key: null, direction: null, type: null };
            }
            return { ...prev, [sortKey]: next };
        });
    };

    const applySort = (rows, cfg) => {
        if (!rows || !cfg.key) return rows || [];
        const arr = [...rows];
        arr.sort((a, b) => {
            const cmp = compareValues(a[cfg.key], b[cfg.key], cfg.type);
            return cfg.direction === "asc" ? cmp : -cmp;
        });
        return arr;
    };

    // Identifica una riga (sintesi: id_operatore; dettaglio: id_operatore+giorno+tipo).
    const matchRiga = (r, target) => {
        if (String(r.id_operatore) !== String(target.id_operatore)) return false;
        if (target.giorno_anomalia !== undefined) {
            const tipoKey = sezione === "ore" ? "tipo_controllo" : "tipo_infrazione";
            return (
                r.giorno_anomalia === target.giorno_anomalia &&
                r[tipoKey] === target[tipoKey]
            );
        }
        return true;
    };

    const aggiornaRigaCorrente = (target, patch) => {
        setDati((prev) => {
            const corr = prev[sezione];
            if (!corr) return prev;
            const aggiornate = corr[subTabAttivo].map((r) =>
                matchRiga(r, target) ? { ...r, ...patch } : r
            );
            return {
                ...prev,
                [sezione]: { ...corr, [subTabAttivo]: aggiornate },
            };
        });
    };

    // Propaga il valore di `ignora` su tutte le righe del Dettaglio dell'operatore
    // CHE NON HANNO override esplicito. Le righe con override (`ignora_override=true`)
    // mantengono il loro valore. Solo UI: il backend gia' applica la stessa logica
    // al prossimo Genera tramite arricchisciDettaglio.
    const propagaIgnoraAlDettaglio = (idOperatore, nuovoValore) => {
        setDati((prev) => {
            const corr = prev[sezione];
            if (!corr || !corr.dettaglio) return prev;
            const dettAgg = corr.dettaglio.map((r) => {
                if (String(r.id_operatore) !== String(idOperatore)) return r;
                if (r.ignora_override) return r;
                return { ...r, ignora: nuovoValore };
            });
            return { ...prev, [sezione]: { ...corr, dettaglio: dettAgg } };
        });
    };

    const postIgnora = async (row, patch) => {
        const tipoKey = sezione === "ore" ? "tipo_controllo" : "tipo_infrazione";
        const payload = {
            sezione,
            mese: periodoGeneratoCorrente,
            subTab: subTabAttivo,
            id_operatore: row.id_operatore,
            ignora: patch.ignora !== undefined ? patch.ignora : !!row.ignora,
            note: patch.note !== undefined ? patch.note : row.note || "",
        };
        if (subTabAttivo === "dettaglio") {
            payload.giorno_anomalia = row.giorno_anomalia;
            payload.tipo = row[tipoKey];
        }
        try {
            await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/ignora`,
                payload
            );
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Errore salvataggio";
            setErrore((p) => ({ ...p, [sezione]: msg }));
        }
    };

    const handleToggleIgnora = (row) => {
        const nuovo = !row.ignora;
        if (subTabAttivo === "sintesi") {
            aggiornaRigaCorrente(row, { ignora: nuovo });
            propagaIgnoraAlDettaglio(row.id_operatore, nuovo);
        } else {
            aggiornaRigaCorrente(row, { ignora: nuovo, ignora_override: true });
        }
        postIgnora(row, { ignora: nuovo });
    };

    const handleSaveNota = (row, valore) => {
        const prev = row.note || "";
        if (valore === prev) return;
        const patch = { note: valore };
        if (subTabAttivo === "dettaglio") {
            patch.ignora_override = true;
        }
        aggiornaRigaCorrente(row, patch);
        postIgnora(row, { note: valore });
    };

    const handleExportExcel = async () => {
        if (!datiCorrenti || exporting) return;
        setExporting(true);
        try {
            // Sintesi: stessa vista (filtri dropdown + sort + ignorati) mostrata a schermo.
            const sintesiExport = righeSintesiVisibili || [];
            // Dettaglio: i filtri dropdown (filiale/contratto/settore/commessa/sede) esistono
            // solo a livello di sintesi per operatore, quindi si restringe il dettaglio agli
            // operatori rimasti visibili in sintesi, poi si applicano sort + ignorati propri.
            const operatoriVisibili = new Set(sintesiExport.map((r) => String(r.id_operatore)));
            const dettaglioExport = applicaFiltroIgnorati(
                applySort(datiCorrenti.dettaglio, sortDettaglio),
                filtroIgnoratiApplicato
            ).filter((r) => operatoriVisibili.has(String(r.id_operatore)));
            const payload = {
                sezione,
                mese: periodoGeneratoCorrente,
                sintesi: sintesiExport,
                dettaglio: dettaglioExport,
            };
            const response = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/export`,
                payload,
                { responseType: "blob" }
            );
            const NOMI_FILE = {
                ore: `test_convalida_ore_${periodoGeneratoCorrente}.xlsx`,
                buoniPasto: `report_buoni_pasto_${periodoGeneratoCorrente}.xlsx`,
                buoniNonRend: `buoni_non_rendicontati_${periodoGeneratoCorrente}.xlsx`,
            };
            saveAs(response.data, NOMI_FILE[sezione]);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Errore durante l'esportazione";
            setErrore((p) => ({ ...p, [sezione]: msg }));
        } finally {
            setExporting(false);
        }
    };

    // Export "Rimborsi" (sezione Buoni non rendicontati): foglio/PDF con l'INSIEME
    // completo dei buoni pasto del mese per operatore — rendicontati su Gecos +
    // spettanti non rendicontati — al netto degli ignorati (sempre esclusi, a
    // prescindere dalla tendina Ignorati). Nr. Pasto = somma dei buoni NON ignorati
    // (dal Dettaglio), per i soli operatori che passano i filtri dropdown della
    // tabella. Valore/importo flat 5,2 e totali calcolati lato backend.
    const handleExportRimborsi = async (formato) => {
        if (exporting || !datiCorrenti?.sintesi || !datiCorrenti?.dettaglio) return;
        setExporting(true);
        try {
            const f = filtriCorrenti;
            const operatoriVisibili = new Set(
                datiCorrenti.sintesi
                    .filter(
                        (r) =>
                            matchMultiCampo(r.codici_filiali, f.filiale) &&
                            matchMultiCampo(r.tipo_contratto, f.tipoContratto) &&
                            matchMultiCampo(r.sedi_lavoro, f.sedeLavoro) &&
                            matchMultiCampo(r.settore, f.settore) &&
                            matchMultiCampo(r.commessa, f.commessa)
                    )
                    .map((r) => String(r.id_operatore))
            );
            // Somma per operatore dei buoni non ignorati (rendicontati + spettanti).
            const perOp = new Map();
            for (const d of datiCorrenti.dettaglio) {
                if (d.ignora) continue;
                const id = String(d.id_operatore);
                if (!operatoriVisibili.has(id)) continue;
                const e = perOp.get(id) || { cognome: d.cognome, nome: d.nome, nrPasto: 0 };
                e.nrPasto += Number(d.n_buoni) || 0;
                perOp.set(id, e);
            }
            const righe = [...perOp.values()].filter((r) => r.nrPasto > 0);
            const response = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/rimborsi/export-${formato}`,
                { mese: periodoGeneratoCorrente, righe },
                { responseType: "blob" }
            );
            const ext = formato === "pdf" ? "pdf" : "xlsx";
            saveAs(response.data, `rimborsi_buoni_pasto_${periodoGeneratoCorrente}.${ext}`);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Errore durante l'esportazione";
            setErrore((p) => ({ ...p, [sezione]: msg }));
        } finally {
            setExporting(false);
        }
    };

    // Colonne per la tabella Sintesi (sempre visibile) e per la Dettaglio (modale).
    const COLONNE_SEZIONE = {
        ore: { sintesi: COLONNE_ORE_SINTESI, dettaglio: COLONNE_ORE_DETTAGLIO },
        buoniPasto: { sintesi: COLONNE_BUONI_SINTESI, dettaglio: COLONNE_BUONI_DETTAGLIO },
        buoniNonRend: {
            sintesi: COLONNE_BUONI_NON_REND_SINTESI,
            dettaglio: COLONNE_BUONI_NON_REND_DETTAGLIO,
        },
    };
    // La sezione "oreNotturne" è autocontenuta (SezioneOreNotturne) e non usa queste
    // mappe: fallback difensivi per non rompere i derivati globali.
    const colonneSintesi = (COLONNE_SEZIONE[sezione] || {}).sintesi || [];
    const colonneDettaglio = (COLONNE_SEZIONE[sezione] || {}).dettaglio || [];

    const filtriCorrenti = filtri[sezione];                 // bozza (legata ai dropdown)
    const filtroIgnorati = filtriCorrenti?.ignorati || "tutti";
    const filtriApplicatiCorrenti = filtriApplicati[sezione]; // applicati alla tabella
    const filtroIgnoratiApplicato = filtriApplicatiCorrenti?.ignorati || "tutti";
    const ricercaCorrente = ricercaOperatore[sezione] || ""; // testo ricerca della sezione attiva

    // Estrai valori distinti da un campo comma-joined ("a, b, c") delle righe.
    const estraiOpzioniMulti = (rows, campo) => {
        if (!rows) return [];
        const set = new Set();
        for (const r of rows) {
            const raw = r[campo];
            if (!raw) continue;
            String(raw)
                .split(/,\s*/)
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((v) => set.add(v));
        }
        return [...set].sort((a, b) => a.localeCompare(b, "it-IT", { numeric: true }));
    };

    // Opzioni dropdown calcolate dinamicamente dal dataset corrente.
    // Filiale e Tipo Contratto esistono in tutte le sezioni tabellari (ore + buoni):
    // i service buoni espongono codici_filiali/tipo_contratto con gli stessi nomi campo.
    const opzioniFiliale = useMemo(
        () =>
            estraiOpzioniMulti(datiCorrenti?.sintesi, "codici_filiali").map((v) => ({
                value: v,
                label: v,
            })),
        [datiCorrenti]
    );
    const opzioniTipoContratto = useMemo(
        () =>
            estraiOpzioniMulti(datiCorrenti?.sintesi, "tipo_contratto").map((v) => ({
                value: v,
                label: v,
            })),
        [datiCorrenti]
    );
    // Settore/Commessa esistono sia in sezione "ore" che "buoniPasto".
    const opzioniSettore = useMemo(
        () =>
            estraiOpzioniMulti(datiCorrenti?.sintesi, "settore").map((v) => ({
                value: v,
                label: v,
            })),
        [datiCorrenti]
    );
    const opzioniCommessa = useMemo(
        () =>
            estraiOpzioniMulti(datiCorrenti?.sintesi, "commessa").map((v) => ({
                value: v,
                label: v,
            })),
        [datiCorrenti]
    );
    const opzioniSedeLavoro = useMemo(
        () =>
            sezione === "buoniPasto" || sezione === "buoniNonRend"
                ? estraiOpzioniMulti(datiCorrenti?.sintesi, "sedi_lavoro").map((v) => ({
                      value: v,
                      label: v,
                  }))
                : [],
        [sezione, datiCorrenti]
    );

    // OR match: il valore della riga (comma-joined) intercetta almeno un valore selezionato.
    const matchMultiCampo = (rigaValue, selezionati) => {
        if (!selezionati || selezionati.length === 0) return true;
        const setSel = new Set(selezionati.map((o) => o.value));
        const opVals = String(rigaValue || "")
            .split(/,\s*/)
            .map((s) => s.trim())
            .filter(Boolean);
        return opVals.some((v) => setSel.has(v));
    };

    // Righe della tabella Sintesi: filtri dropdown -> sort -> filtro ignora.
    const righeSintesiVisibili = useMemo(() => {
        if (!datiCorrenti?.sintesi) return null;
        let arr = datiCorrenti.sintesi;
        if (sezione === "ore") {
            const { filiale, tipoContratto, settore, commessa } = filtriApplicatiCorrenti;
            arr = arr.filter(
                (r) =>
                    matchMultiCampo(r.codici_filiali, filiale) &&
                    matchMultiCampo(r.tipo_contratto, tipoContratto) &&
                    matchMultiCampo(r.settore, settore) &&
                    matchMultiCampo(r.commessa, commessa)
            );
        } else {
            const { filiale, tipoContratto, sedeLavoro, settore, commessa } = filtriApplicatiCorrenti;
            arr = arr.filter(
                (r) =>
                    matchMultiCampo(r.codici_filiali, filiale) &&
                    matchMultiCampo(r.tipo_contratto, tipoContratto) &&
                    matchMultiCampo(r.sedi_lavoro, sedeLavoro) &&
                    matchMultiCampo(r.settore, settore) &&
                    matchMultiCampo(r.commessa, commessa)
            );
        }
        // Ricerca testuale live (nome, cognome, id operatore): ogni token separato da
        // spazio deve comparire in "nome cognome id". Case-insensitive e indipendente
        // dall'ordine, così "rossi mario" trova comunque "Mario Rossi".
        const q = ricercaCorrente.trim().toLowerCase();
        if (q) {
            const tokens = q.split(/\s+/);
            arr = arr.filter((r) => {
                const hay = `${r.nome || ""} ${r.cognome || ""} ${r.id_operatore ?? ""}`.toLowerCase();
                return tokens.every((t) => hay.includes(t));
            });
        }
        if (sortSintesi.key) {
            arr = [...arr].sort((a, b) => {
                const cmp = compareValues(a[sortSintesi.key], b[sortSintesi.key], sortSintesi.type);
                return sortSintesi.direction === "asc" ? cmp : -cmp;
            });
        }
        arr = applicaFiltroIgnorati(arr, filtroIgnoratiApplicato);
        return arr;
    }, [datiCorrenti, sortSintesi, filtroIgnoratiApplicato, sezione, filtriApplicatiCorrenti, ricercaCorrente]);

    // Righe del Dettaglio per il modale: filtrate per operatore selezionato,
    // poi filtrate per ignora (toggle), poi ordinate.
    const righeModale = useMemo(() => {
        if (!operatoreModale || !datiCorrenti?.dettaglio) return null;
        const idTarget = String(operatoreModale.id_operatore);
        let arr = datiCorrenti.dettaglio.filter((r) => String(r.id_operatore) === idTarget);
        arr = applicaFiltroIgnorati(arr, filtroIgnoratiApplicato);
        if (sortDettaglio.key) {
            arr = [...arr].sort((a, b) => {
                const cmp = compareValues(a[sortDettaglio.key], b[sortDettaglio.key], sortDettaglio.type);
                return sortDettaglio.direction === "asc" ? cmp : -cmp;
            });
        }
        return arr;
    }, [operatoreModale, datiCorrenti, filtroIgnoratiApplicato, sortDettaglio]);

    // Record da importare in BULK: i giorni NR degli operatori visibili (filtri
    // applicati) e non ignorati, dettaglio non ignorato e non già importato.
    const recordsDaImportare = useMemo(() => {
        if (sezione !== "buoniNonRend" || !datiCorrenti?.dettaglio) return [];
        const opsVisibili = new Set(
            (righeSintesiVisibili || []).filter((r) => !r.ignora).map((r) => String(r.id_operatore))
        );
        return datiCorrenti.dettaglio
            .filter(
                (r) =>
                    opsVisibili.has(String(r.id_operatore)) && !r.ignora && !r.importato
            )
            .map((r) => ({ id_operatore: r.id_operatore, giorno: r.giorno_anomalia }));
    }, [sezione, datiCorrenti, righeSintesiVisibili]);

    const LEGENDA_SEZIONE = {
        ore: { voci: LEGENDA_ORE, titolo: "Legenda — Rendicontazione Ore" },
        buoniPasto: { voci: LEGENDA_BUONI, titolo: "Legenda — Rendicontazione Buoni Pasto" },
        buoniNonRend: {
            voci: LEGENDA_BUONI_NON_REND,
            titolo: "Legenda — Buoni pasto",
        },
        multe: { descrizione: GUIDA_MULTE, titolo: "Guida — Rendicontazione Multe" },
    };
    const legendaVoci = (LEGENDA_SEZIONE[sezione] || {}).voci || [];
    const legendaTitolo = (LEGENDA_SEZIONE[sezione] || {}).titolo || "";
    const legendaDescrizione = (LEGENDA_SEZIONE[sezione] || {}).descrizione || null;

    return (
        <div className="acr-page">
            <header className="acr-header">
                <div className="acr-header-content">
                    <div className="acr-header-title">
                        <div className="acr-title-row">
                            <h1>Agente Controllo Rendicontazioni</h1>
                            <button
                                className="acr-info-btn"
                                onClick={() => setLegendaAperta(true)}
                                title="Mostra legenda"
                                aria-label="Mostra legenda"
                            >
                                <span>i</span>
                            </button>
                        </div>
                        <p className="acr-subtitle">
                            Validazione automatica della rendicontazione mensile di ore lavorate, buoni pasto e ore notturne, ed estrazione e rendicontazione delle multe stradali
                        </p>
                    </div>
                </div>
            </header>

            <main className="acr-main">
                <div className="acr-tabs-macro">
                    <button
                        className={`acr-tab-macro ${sezione === "ore" ? "active" : ""}`}
                        onClick={() => {
                            if (sezione === "ore") return;
                            setSezione("ore");
                            setOperatoreModale(null);
                            setModaleFullscreen(false);
                        }}
                    >
                        Rendicontazione Ore
                    </button>
                    <button
                        className={`acr-tab-macro ${
                            sezione === "buoniPasto" || sezione === "buoniNonRend" ? "active" : ""
                        }`}
                        onClick={() => {
                            if (sezione === "buoniPasto" || sezione === "buoniNonRend") return;
                            setSezione("buoniPasto");
                            setOperatoreModale(null);
                            setModaleFullscreen(false);
                        }}
                    >
                        Rendicontazione Buoni Pasto
                    </button>
                    <button
                        className={`acr-tab-macro ${sezione === "oreNotturne" ? "active" : ""}`}
                        onClick={() => {
                            if (sezione === "oreNotturne") return;
                            setSezione("oreNotturne");
                            setOperatoreModale(null);
                            setModaleFullscreen(false);
                        }}
                    >
                        Rendicontazione Ore Notturne
                    </button>
                    <button
                        className={`acr-tab-macro ${sezione === "multe" ? "active" : ""}`}
                        onClick={() => {
                            if (sezione === "multe") return;
                            setSezione("multe");
                            setOperatoreModale(null);
                            setModaleFullscreen(false);
                        }}
                    >
                        Rendicontazione Multe
                    </button>
                </div>

                {sezione === "oreNotturne" ? (
                    <SezioneOreNotturne />
                ) : sezione === "multe" ? (
                    <SezioneMulte />
                ) : (
                <>
                {(sezione === "buoniPasto" || sezione === "buoniNonRend") && (
                    <div className="acr-tabs-sub">
                        <button
                            className={`acr-tab-sub ${sezione === "buoniPasto" ? "active" : ""}`}
                            onClick={() => {
                                if (sezione === "buoniPasto") return;
                                setSezione("buoniPasto");
                                setOperatoreModale(null);
                                setModaleFullscreen(false);
                            }}
                        >
                            Sintesi Errori
                        </button>
                        <button
                            className={`acr-tab-sub ${sezione === "buoniNonRend" ? "active" : ""}`}
                            onClick={() => {
                                if (sezione === "buoniNonRend") return;
                                setSezione("buoniNonRend");
                                setOperatoreModale(null);
                                setModaleFullscreen(false);
                            }}
                        >
                            Buoni pasto
                        </button>
                    </div>
                )}

                <div className="acr-panel">
                    <div className="acr-filters-grid">
                        <div className="acr-filter-item">
                            <label>Periodo</label>
                            <MonthPickerModal
                                value={periodo}
                                onChange={(e) => setPeriodo(e.target.value)}
                                label="Seleziona mese"
                                min="2026-01"
                            />
                        </div>

                        <div className="acr-filter-item">
                            <label>Cerca operatore</label>
                            <input
                                type="text"
                                className="acr-filter-search"
                                value={ricercaCorrente}
                                onChange={(e) =>
                                    setRicercaOperatore((p) => ({ ...p, [sezione]: e.target.value }))
                                }
                                placeholder={datiCorrenti ? "Nome, cognome o ID…" : "Genera report…"}
                                disabled={!datiCorrenti}
                            />
                        </div>

                        {sezione === "ore" && (
                            <>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Filiale</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniFiliale}
                                        value={filtriCorrenti.filiale}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                ore: { ...p.ore, filiale: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutte" : "Genera report…"}
                                        noOptionsMessage={() => "Nessuna filiale"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Tipo Contratto</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniTipoContratto}
                                        value={filtriCorrenti.tipoContratto}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                ore: { ...p.ore, tipoContratto: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutti" : "Genera report…"}
                                        noOptionsMessage={() => "Nessun tipo"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Settore</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniSettore}
                                        value={filtriCorrenti.settore}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                ore: { ...p.ore, settore: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutti" : "Genera report…"}
                                        noOptionsMessage={() => "Nessun settore"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Commessa</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniCommessa}
                                        value={filtriCorrenti.commessa}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                ore: { ...p.ore, commessa: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutte" : "Genera report…"}
                                        noOptionsMessage={() => "Nessuna commessa"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                            </>
                        )}

                        {(sezione === "buoniPasto" || sezione === "buoniNonRend") && (
                            <>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Filiale</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniFiliale}
                                        value={filtriCorrenti.filiale}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                [sezione]: { ...p[sezione], filiale: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutte" : "Genera report…"}
                                        noOptionsMessage={() => "Nessuna filiale"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Tipo Contratto</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniTipoContratto}
                                        value={filtriCorrenti.tipoContratto}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                [sezione]: { ...p[sezione], tipoContratto: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutti" : "Genera report…"}
                                        noOptionsMessage={() => "Nessun tipo"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Sede di lavoro</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniSedeLavoro}
                                        value={filtriCorrenti.sedeLavoro}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                [sezione]: { ...p[sezione], sedeLavoro: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutte" : "Genera report…"}
                                        noOptionsMessage={() => "Nessuna sede"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Settore</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniSettore}
                                        value={filtriCorrenti.settore}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                [sezione]: { ...p[sezione], settore: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutti" : "Genera report…"}
                                        noOptionsMessage={() => "Nessun settore"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                                <div className="acr-filter-item acr-filter-item-select">
                                    <label>Commessa</label>
                                    <Select
                                        isMulti
                                        isDisabled={!datiCorrenti}
                                        options={opzioniCommessa}
                                        value={filtriCorrenti.commessa}
                                        onChange={(vals) =>
                                            setFiltri((p) => ({
                                                ...p,
                                                [sezione]: { ...p[sezione], commessa: vals || [] },
                                            }))
                                        }
                                        placeholder={datiCorrenti ? "Tutte" : "Genera report…"}
                                        noOptionsMessage={() => "Nessuna commessa"}
                                        classNamePrefix="acr-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                            </>
                        )}

                        <div className="acr-filter-item acr-filter-item-select">
                            <label>Ignorati</label>
                            <Select
                                isDisabled={!datiCorrenti}
                                options={OPZIONI_IGNORATI}
                                value={
                                    OPZIONI_IGNORATI.find((o) => o.value === filtroIgnorati) ||
                                    OPZIONI_IGNORATI[0]
                                }
                                onChange={(opt) =>
                                    setFiltri((p) => ({
                                        ...p,
                                        [sezione]: {
                                            ...p[sezione],
                                            ignorati: opt ? opt.value : "tutti",
                                        },
                                    }))
                                }
                                isSearchable={false}
                                classNamePrefix="acr-select"
                            />
                        </div>

                        <div className="acr-filter-actions">
                            <button
                                className="acr-btn-primary"
                                onClick={() => handleApplica()}
                                disabled={!periodo || loadingCorrente}
                            >
                                {loadingCorrente ? "Caricamento…" : "Applica"}
                            </button>
                            {sezione === "buoniNonRend" && datiCorrenti && (
                                <button
                                    className="acr-btn-importa"
                                    onClick={() =>
                                        apriConfermaImport(
                                            recordsDaImportare,
                                            "__bulk__",
                                            `Stai per scrivere su Gecos ${recordsDaImportare.length} buoni pasto per tutti gli operatori mostrati (non ignorati). Continuare?`
                                        )
                                    }
                                    disabled={importingGecos || recordsDaImportare.length === 0}
                                    title="Scrive su Gecos i buoni pasto per tutti i giorni mostrati e non ignorati"
                                >
                                    {importingGecos ? (
                                        <>
                                            <span className="acr-spinner" aria-hidden="true" />
                                            Importazione…
                                        </>
                                    ) : (
                                        `Importa su Gecos${recordsDaImportare.length ? ` (${recordsDaImportare.length})` : ""}`
                                    )}
                                </button>
                            )}
                            <button
                                className="acr-btn-mail"
                                disabled
                                title="Funzionalità in arrivo"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                </svg>
                                Invia Mail
                            </button>
                        </div>
                    </div>
                </div>

                {erroreCorrente && (
                    <div className="acr-errore">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                        {erroreCorrente}
                    </div>
                )}

                {importMsg && (
                    <div className={`acr-import-msg ${importMsg.ok ? "ok" : "ko"}`}>
                        {importMsg.testo}
                    </div>
                )}

                {loadingCorrente && (
                    <Loader messaggio={`Analisi ${sezione === "ore" ? "ore" : "buoni pasto"} per ${periodoLabel(periodo)}…`} />
                )}

                {!loadingCorrente && !datiCorrenti && !erroreCorrente && (
                    <div className="acr-empty-state">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                            <polyline points="9 14 11 16 15 12" />
                        </svg>
                        <p>Seleziona il periodo e premi <strong>Applica</strong> per aggiornare l'analisi.</p>
                    </div>
                )}

                {!loadingCorrente && datiCorrenti && (
                    <>
                        <div className="acr-info-periodo">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            Report del periodo <strong>{periodoLabel(periodoGeneratoCorrente)}</strong>
                            {" — "}
                            {sezione === "ore"
                                ? `${datiCorrenti.sintesi.length} operatori · ${datiCorrenti.dettaglio.length} anomalie`
                                : sezione === "buoniNonRend"
                                ? `${datiCorrenti.sintesi.length} operatori · ${datiCorrenti.dettaglio.length} buoni pasto`
                                : `${datiCorrenti.sintesi.length} operatori con infrazioni · ${datiCorrenti.dettaglio.length} infrazioni`}
                        </div>

                        <div className="acr-toolbar-sintesi">
                            <div className="acr-toolbar-title">
                                {(() => {
                                    const tot = datiCorrenti.sintesi.length;
                                    const vis = righeSintesiVisibili?.length ?? 0;
                                    const titolo = sezione === "buoniPasto" ? "Sintesi Errori" : "Sintesi";
                                    return vis === tot
                                        ? `${titolo} (${tot})`
                                        : `${titolo} (${vis} su ${tot})`;
                                })()}
                            </div>
                            <div className="acr-actions-right">
                                <button
                                    className="acr-export-btn"
                                    onClick={
                                        sezione === "buoniNonRend"
                                            ? () => handleExportRimborsi("excel")
                                            : handleExportExcel
                                    }
                                    disabled={exporting}
                                    title={
                                        sezione === "buoniNonRend"
                                            ? "Esporta il report rimborsi in Excel (Operatore, Valore, Nr. Pasto, Importo)"
                                            : "Esporta i dati in Excel (Sintesi, Dettaglio, Legenda)"
                                    }
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    {exporting ? "Esportazione…" : "Esporta Excel"}
                                </button>
                                {sezione === "buoniNonRend" && (
                                    <button
                                        className="acr-export-btn acr-export-btn-pdf"
                                        onClick={() => handleExportRimborsi("pdf")}
                                        disabled={exporting}
                                        title="Esporta il report rimborsi in PDF (layout cartaceo)"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line x1="9" y1="13" x2="15" y2="13" />
                                            <line x1="9" y1="17" x2="15" y2="17" />
                                        </svg>
                                        {exporting ? "Esportazione…" : "Esporta PDF"}
                                    </button>
                                )}
                                <button
                                    className="acr-fullscreen-btn"
                                    onClick={() => setIsFullscreen(true)}
                                    title="Tutto schermo"
                                    aria-label="Tutto schermo"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 3 21 3 21 9" />
                                        <polyline points="9 21 3 21 3 15" />
                                        <line x1="21" y1="3" x2="14" y2="10" />
                                        <line x1="3" y1="21" x2="10" y2="14" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <RotateDevice active={isFullscreen} />
                        <Tabella
                            colonne={colonneSintesi}
                            righe={righeSintesiVisibili}
                            vuotoLabel="Nessun operatore con anomalie in questo periodo."
                            onBadgeClick={() => setLegendaAperta(true)}
                            sortConfig={sortSintesi}
                            onSort={handleSort}
                            isFullscreen={isFullscreen}
                            onExitFullscreen={() => setIsFullscreen(false)}
                            onToggleIgnora={handleToggleIgnora}
                            onSaveNota={handleSaveNota}
                            onImporta={sezione === "buoniNonRend" ? handleImportaRiga : undefined}
                            importingKey={importingKey}
                            onRowClick={(row) =>
                                setOperatoreModale({
                                    id_operatore: row.id_operatore,
                                    nome: row.nome,
                                    cognome: row.cognome,
                                })
                            }
                        />
                    </>
                )}
                </>
                )}
            </main>

            <ModaleLegenda
                aperta={legendaAperta}
                voci={legendaVoci}
                descrizione={legendaDescrizione}
                titolo={legendaTitolo}
                onClose={() => setLegendaAperta(false)}
            />

            <ModaleDettaglioOperatore
                aperto={!!operatoreModale}
                operatore={operatoreModale}
                periodoLabel={periodoLabel(periodoGeneratoCorrente)}
                colonne={colonneDettaglio}
                righe={righeModale}
                sortConfig={sortDettaglio}
                onSort={handleSort}
                onBadgeClick={() => setLegendaAperta(true)}
                onToggleIgnora={handleToggleIgnora}
                onSaveNota={handleSaveNota}
                onImporta={sezione === "buoniNonRend" ? handleImportaRiga : undefined}
                importingKey={importingKey}
                isFullscreen={modaleFullscreen}
                onToggleFullscreen={() => setModaleFullscreen((v) => !v)}
                onClose={() => {
                    setOperatoreModale(null);
                    setModaleFullscreen(false);
                }}
            />

            {confirmImport && (
                <div className="acr-confirm-overlay" onClick={() => setConfirmImport(null)}>
                    <div className="acr-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="acr-confirm-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                        </div>
                        <h3>Conferma import su Gecos</h3>
                        <p>{confirmImport.messaggio}</p>
                        <div className="acr-confirm-actions">
                            <button type="button" className="acr-btn-secondary" onClick={() => setConfirmImport(null)}>
                                Annulla
                            </button>
                            <button type="button" className="acr-btn-importa" onClick={confermaImportGecos}>
                                Conferma
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgenteControlloRendicontazioni;
